import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { getUsers, createUser, updateUser, resetUserPassword } from "@/lib/api";

const ROLES = [
  { value: "admin", label: "Owner/Admin", color: "bg-orange-100 text-orange-700" },
  { value: "accountant", label: "Accountant", color: "bg-blue-100 text-blue-700" },
  { value: "delivery", label: "Delivery Boy", color: "bg-green-100 text-green-700" },
  { value: "viewer", label: "Viewer", color: "bg-zinc-100 text-zinc-700" },
];

const emptyForm = { email: "", password: "", name: "", phone: "", role: "viewer" };

export default function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [resetOpen, setResetOpen] = useState(null);
  const [resetPw, setResetPw] = useState("password123");

  useEffect(() => { getUsers().then(setUsers).catch(() => {}); }, []);

  const openAdd = () => { setEditingId(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (u) => { setEditingId(u._id); setForm({ email: u.email, password: "", name: u.name || "", phone: u.phone || "", role: u.role || "viewer" }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email required"); return; }
    if (!editingId && !form.password.trim()) { toast.error("Password required for new user"); return; }
    if (editingId) {
      await updateUser(editingId, { name: form.name, phone: form.phone, role: form.role });
    } else {
      await createUser(form);
    }
    setUsers(await getUsers());
    setDialogOpen(false);
    toast.success(editingId ? "User updated" : "User created");
  };

  const handleToggleActive = async (u) => {
    await updateUser(u._id, { isActive: !u.isActive });
    setUsers(await getUsers());
    toast.success(`User ${u.isActive ? "deactivated" : "activated"}`);
  };

  const handleResetPw = async () => {
    if (!resetOpen) return;
    await resetUserPassword(resetOpen._id, resetPw);
    setResetOpen(null);
    setResetPw("password123");
    toast.success("Password reset successfully");
  };

  const getRoleBadge = (role) => {
    const r = ROLES.find((x) => x.value === role) || ROLES[3];
    return <Badge className={`text-[10px] ${r.color}`}>{r.label}</Badge>;
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1200px] mx-auto" data-testid="manage-users-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-black text-zinc-900">Manage Users</h1>
        <Button data-testid="add-user-button" onClick={openAdd} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm">
          <Plus className="w-4 h-4 mr-2" /> Add User
        </Button>
      </div>

      <Card className="border border-zinc-200 rounded-sm shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="text-xs font-bold uppercase tracking-wider text-zinc-500">Name</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-zinc-500">Email</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-zinc-500">Phone</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-zinc-500">Role</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-zinc-500">Status</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-zinc-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-zinc-400 py-12">No users found.</TableCell></TableRow>
              ) : users.map((u) => (
                <TableRow key={u._id} data-testid={`user-row-${u._id}`}>
                  <TableCell className="font-medium text-sm">{u.name}</TableCell>
                  <TableCell className="text-sm font-mono">{u.email}</TableCell>
                  <TableCell className="text-sm font-mono">{u.phone || "—"}</TableCell>
                  <TableCell>{getRoleBadge(u.role)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch data-testid={`toggle-active-${u._id}`} checked={u.isActive !== false} onCheckedChange={() => handleToggleActive(u)} />
                      <span className="text-xs text-zinc-500">{u.isActive !== false ? "Active" : "Inactive"}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u._id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => setResetOpen(u)} data-testid={`reset-pw-${u._id}`}><KeyRound className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{editingId ? "Edit User" : "Create User"}</DialogTitle>
            <DialogDescription>{editingId ? "Update user details." : "Add a new team member."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Name *</Label><Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
            <div><Label>Email *</Label><Input data-testid="user-email-input" type="email" value={form.email} disabled={!!editingId} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
            {!editingId && <div><Label>Password *</Label><Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>}
            <div><Label>Phone</Label><Input data-testid="user-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="bg-zinc-50 border-zinc-300 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-user-button" onClick={handleSave} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm">{editingId ? "Update" : "Create User"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetOpen} onOpenChange={() => setResetOpen(null)}>
        <DialogContent className="max-w-sm rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Reset Password</DialogTitle>
            <DialogDescription>Set new password for {resetOpen?.name}</DialogDescription>
          </DialogHeader>
          <div className="py-2"><Label>New Password</Label><Input data-testid="reset-password-input" type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
          <DialogFooter>
            <Button data-testid="confirm-reset-pw" onClick={handleResetPw} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
